import {
  Alert,
  Center,
  Group,
  Loader,
  MantineProvider,
  Select,
  Text,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
} from '@tabler/icons-react';
import { getCode } from 'country-list';
import { isPossibleNumber } from 'libphonenumber-js';
import Radar from 'radar-sdk-js';
import 'radar-sdk-js/dist/radar.css';
import { useEffect, useRef, useState } from 'react';
import { PhoneInput } from 'react-international-phone';
import { BrowserRouter, Link, Route, Routes } from 'react-router-dom';
import './App.css';
import { DOCUMENT_TEXTS } from './constants/documentTexts';
import Instructions from './Instructions';

// CDN URLs for images
const LOGO_URL = 'https://cdn.prod.website-files.com/66f564eed0595486171edd49/691e0019b62c2cf13ddaa0db_Cancer%20Check%20Test%20Logo_Main.png';
const BANNER_URL = 'https://cdn.prod.website-files.com/66f564eed0595486171edd49/69306ab1cce08535d5848aa0_not%20processed.png';
const QR_SCAN_URL = 'https://cdn.prod.website-files.com/66f564eed0595486171edd49/69306967f7704118a4f85434_scan%20code.png';

// Header Component
const Header = () => (
  <header className="app-header">
    <div className="header-content">
      <a href="https://www.cancerchecklabs.com/">
        <img src={LOGO_URL} alt="Cancer Check" className="logo-img" />
      </a>
    </div>
  </header>
);

// Hero Component
const Hero = () => (
  <section className="hero-section">
    <div className="container">
      <h1>Cancer Check Kit Registration</h1>
      <p className="subtitle">Complete your registration to receive your test results securely and on time.</p>
    </div>
  </section>
);

// Footer Component
const Footer = () => (
  <footer className="app-footer">
    <div className="footer-content">
      <div className="footer-copyright">
        <p>&copy; 2026 Cancer Check Labs. All rights reserved.</p>
      </div>
      <div className="footer-divider"></div>
      <div className="footer-disclaimer">
        <p>This content does not provide medical or healthcare advice. The information, including but not limited to, text, graphics, images and other material contained here are for informational purposes only. No material in this content is intended to be a substitute for professional medical advice, diagnosis or treatment. Always seek the advice of your physician or other qualified healthcare provider with any questions you may have regarding a medical condition or treatment and before undertaking a new health care regimen and never disregard professional medical advice or delay in seeking it because of something you have read in this content.</p>
        <p>Cancer Check Labs' clinical laboratory has earned a Federal Certificate of Compliance under the Clinical Laboratory Improvement Amendments of 1988 (CLIA), which is regulated by the Centers for Medicare and Medicaid Services (CMS). The Cancer Check test is not intended to diagnose, treat, cure, or prevent any disease and should not replace established, evidence-based screening, diagnostic, or medical guidelines for cancer detection. Like all medical tests, it has limitations and may not detect all cancers. While the Cancer Check test has not been cleared or approved by the Food and Drug Administration (FDA), both the FDA and CMS operate under the U.S. Department of Health and Human Services (HHS).</p>
      </div>
    </div>
  </footer>
);

// Need Help Component
const NeedHelp = () => (
  <div className="need-help">
    <h4>Need Help?</h4>
    <p><a href="tel:214-301-0351">214-301-0351</a> | <a href="mailto:hello@cancerchecklabs.com">hello@cancerchecklabs.com</a> | <button type="button" className="link-button" onClick={() => { window.$zoho?.salesiq?.floatwindow?.visible("show"); }}>Chat with us</button></p>
  </div>
);

// Document Modal Component
const DocumentModal = ({ isOpen, onClose, title, content }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className={`modal-overlay ${isOpen ? 'active' : ''}`}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal">
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body" dangerouslySetInnerHTML={{ __html: content }} />
      </div>
    </div>
  );
};

// Kit Status Landing Page Component
const KitStatusLanding = ({ firstName, kitStatus, partnerName, hipaaSigned, resultsSendPatient }) => {
  const CONTACT_ICON_URL = 'https://cdn.prod.website-files.com/66f564eed0595486171edd49/691b93d718664e13feeba58c_contact-us.png';

  // Map raw DB kit_status to landing page step index (0-4)
  // Step 0 = Kit registered (always completed for registered kits)
  // Step 1 = Kit Pending / Kit received
  // Step 2 = Kit processing / Kit processed
  // Step 3 = Pathology review
  // Step 4 = Results
  const getActiveStep = (status) => {
    if (!status) return 1; // registered but no further status yet
    const s = status.toLowerCase();
    if (['kit_order_shipped', 'carrier_outbound_picked_up', 'carrier_outbound_out_for_delivery',
         'carrier_outbound_delivered', 'carrier_inbound_picked_up',
         'carrier_inbound_out_for_delivery', 'carrier_inbound_delivered'].includes(s)) return 1;
    if (s === 'accessioned') return 2;
    if (s === 'preprocessing' || s === 'processing') return 2;
    if (s === 'in_pathology_review') return 3;
    if (s === 'result_sent') return 5; // beyond last step, marks all steps completed
    return 1; // default for unknown statuses
  };

  const activeStep = getActiveStep(kitStatus);

  const getStepState = (stepIndex) => {
    if (stepIndex < activeStep) return 'completed';
    if (stepIndex === activeStep) return 'active';
    return 'pending';
  };

  // Results messaging
  const getResultsMessage = () => {
    const hasPartner = !!partnerName;
    if (hasPartner && hipaaSigned && resultsSendPatient) {
      return `Results will be emailed to you and ${partnerName} when they are complete`;
    }
    if (hasPartner && hipaaSigned) {
      return `Results will be sent to ${partnerName}`;
    }
    return 'Results will be emailed to you when they are complete';
  };

  // Step definitions with dynamic labels
  const steps = [
    {
      title: 'Kit registered',
      subtitle: 'Registration complete',
    },
    {
      title: getStepState(1) === 'completed' ? 'Kit received' : 'Kit Pending',
      subtitle: getStepState(1) === 'completed' ? 'Kit received at our lab' : 'Awaiting kit at lab',
    },
    {
      title: getStepState(2) === 'completed' ? 'Kit processed' : 'Kit processing',
      subtitle: getStepState(2) === 'completed' ? 'Processing complete' : null,
    },
    {
      title: 'Pathology review',
      subtitle: getStepState(3) === 'completed' ? 'Pathologist report complete' : null,
    },
    {
      title: 'Results',
      subtitle: null,
      hasTooltip: true,
    },
  ];

  const CheckSvg = () => (
    <svg viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
  );

  return (
    <div className="kit-status-page-wrapper">
      <div className="kit-status-container">
        {/* Hero */}
        <div className="kit-status-hero">
          <div className="kit-status-hero-inner">
            <h1>{firstName ? `${firstName}'s Test Kit Status` : 'Test Kit Status'}</h1>
            <p>Track the kit's progress in real-time.</p>
            <div className="kit-status-workflow-icon">
              <img src={process.env.PUBLIC_URL + '/workflow.png'} alt="Kit workflow" />
            </div>
          </div>
        </div>

        {/* Progress Tracker */}
        <div className="kit-status-content">
          <div className="kit-status-progress-tracker">
            {steps.map((step, index) => {
              const state = getStepState(index);
              const isLast = index === steps.length - 1;

              return (
                <div className="kit-status-step" key={index}>
                  <div className="kit-status-step-icon-col">
                    <div className={`kit-status-step-icon ${state}`}>
                      <CheckSvg />
                    </div>
                    {!isLast && (
                      <div className={`kit-status-step-connector ${state === 'completed' ? 'completed' : 'pending'}`} />
                    )}
                  </div>
                  <div className="kit-status-step-content">
                    <div className={`kit-status-step-title ${state === 'pending' ? 'pending' : ''}`}>
                      {step.title}
                      {state === 'active' && (
                        <span className="kit-status-current-step-badge">Current Step</span>
                      )}
                      {step.hasTooltip && (
                        <span className="kit-status-info-icon-wrapper">
                          <span className="kit-status-info-icon">i</span>
                          <span className="kit-status-tooltip">{getResultsMessage()}</span>
                        </span>
                      )}
                    </div>
                    {step.subtitle && (
                      <p className="kit-status-step-subtitle">{step.subtitle}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Info Banner */}
          <div className="kit-status-info-banner">
            <div className="kit-status-info-icon-lg">
              <img src={process.env.PUBLIC_URL + '/info.png'} alt="Info" />
            </div>
            <p>Your kit is progressing through the workflow. Check in to see its status at any time.</p>
          </div>

          {/* Contact Banner */}
          <div className="kit-status-contact-banner">
            <div className="kit-status-contact-inner">
              <span className="kit-status-contact-label">Contact Us:</span>
              <span className="kit-status-contact-info">
                <img src={CONTACT_ICON_URL} alt="Contact" width="24" style={{ width: '24px', height: 'auto' }} />
                <a href="tel:214-301-0351">214-301-0351</a> | <a href="mailto:hello@cancerchecklabs.com">hello@cancerchecklabs.com</a>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const KitRegistrationApp = () => {
  const [step, setStep] = useState(0);
  const [kitId, setKitId] = useState('');
  const [kitData, setKitData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [kitError, setKitError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [signature, setSignature] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
    phone: '',
    email: '',
    confirmEmail: '',
    dateOfBirth: '',
    sexAtBirth: ''
  });
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [completedKitId, setCompletedKitId] = useState('');
  const [errors, setErrors] = useState({});
  const [modalOpen, setModalOpen] = useState(null);
  const [hasCustomerDataPrefill, setHasCustomerDataPrefill] = useState(false);
  const [isReadOnlyMode, setIsReadOnlyMode] = useState(false);
  const [showStatusLanding, setShowStatusLanding] = useState(false);

  // Character-by-character kit ID input state (16 characters)
  const [kitChars, setKitChars] = useState(Array(16).fill(''));
  const kitInputRefs = useRef([]);

  // Ref for address input to remove focus after selection
  const addressInputRef = useRef(null);
  // Ref to track when an address is being selected (prevents autocomplete from re-triggering)
  const isSelectingAddress = useRef(false);

  // Signature boolean variables
  const [qualificationSigned, setQualificationSigned] = useState(false);
  const [authorizationSigned, setAuthorizationSigned] = useState(false);
  const [hipaaSigned, setHipaaSigned] = useState(false);

  // HIPAA opt-in state (unchecked by default; customer opts in if desired)
  const [hipaaOptIn, setHipaaOptIn] = useState(false);
  const [showHipaaWarning, setShowHipaaWarning] = useState(false);

  // Initialize Radar and handle URL parameters
  useEffect(() => {
    console.log('🔧 Initializing Radar with token:', process.env.REACT_APP_RADAR_TOKEN);
    try {
      Radar.initialize(`${process.env.REACT_APP_RADAR_TOKEN}`);
      console.log('✅ Radar initialized successfully');
    } catch (error) {
      console.error('❌ Error initializing Radar:', error);
    }

    // Check for kit_id in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const kitIdFromUrl = urlParams.get('kit_id');

    // On mobile, scroll down slightly so the Continue button sits above the iPhone search bar
    if (!kitIdFromUrl) {
      setTimeout(() => window.scrollTo({ top: 25, behavior: 'smooth' }), 300);
    }

    if (kitIdFromUrl) {
      console.log('📱 Kit ID found in URL:', kitIdFromUrl);
      // Parse the kit ID and fill in the character inputs
      const cleanId = kitIdFromUrl.toUpperCase().replace(/[^A-Z0-9]/g, '');
      // Remove the 'S' prefix if present
      const chars = cleanId.startsWith('S') ? cleanId.slice(1) : cleanId;
      const charArray = chars.slice(0, 16).split('');
      setKitChars(prev => {
        const newChars = [...prev];
        charArray.forEach((char, i) => {
          if (i < 16) newChars[i] = char;
        });
        return newChars;
      });
      setKitId(kitIdFromUrl);
      // Auto-validate the kit
      validateKit(kitIdFromUrl);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize Zoho SalesIQ chatbot
  useEffect(() => {
    window.$zoho = window.$zoho || {};
    window.$zoho.salesiq = window.$zoho.salesiq || {
      ready: function() {}
    };

    const script = document.createElement('script');
    script.id = 'zsiqscript';
    script.src = 'https://salesiq.zohopublic.com/widget?wc=siqbe1bdd8b08c36a8c28ce17f284c47f553c32edc9ad81c1a87e87f7c763ce3d8f';
    script.defer = true;
    document.body.appendChild(script);

    console.log('✅ Zoho SalesIQ chatbot initialized');

    return () => {
      const existingScript = document.getElementById('zsiqscript');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  // Sync kitChars to kitId
  useEffect(() => {
    const fullId = `S-${kitChars.slice(0, 4).join('')}-${kitChars.slice(4, 8).join('')}-${kitChars.slice(8, 12).join('')}-${kitChars.slice(12, 16).join('')}`;
    setKitId(fullId);
  }, [kitChars]);

  // Scroll to top on step transitions so mobile users don't stay parked at the bottom
  // where the Continue button lives.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step, registrationComplete, showStatusLanding]);

  // Address autocomplete effect
  useEffect(() => {
    if (isReadOnlyMode) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    if (isSelectingAddress.current) {
      isSelectingAddress.current = false;
      return;
    }

    if (customerInfo.address1.length < 3) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const timeoutId = setTimeout(() => {
      try {
        Radar.autocomplete({ query: customerInfo.address1 })
          .then((result) => {
            setLoading(false);
            const addresses = result?.addresses || [];
            setSuggestions(addresses);
          })
          .catch((err) => {
            setLoading(false);
            console.error('Radar promise error:', err);
            setSuggestions([]);
          });
      } catch (error) {
        console.error('Error calling Radar.autocomplete:', error);
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [customerInfo.address1, isReadOnlyMode]);

  // Auto-show suggestions when they arrive
  useEffect(() => {
    if (suggestions.length > 0 && customerInfo.address1.length >= 3) {
      setShowSuggestions(true);
    }
  }, [suggestions, customerInfo.address1]);

  // Kit character input handlers
  const handleCharInput = (index, value) => {
    const char = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (char.length <= 1) {
      const newChars = [...kitChars];
      newChars[index] = char;
      setKitChars(newChars);

      // Auto-advance to next input
      if (char.length === 1 && index < 15) {
        kitInputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleCharKeyDown = (index, e) => {
    if (e.key === 'Backspace' && kitChars[index] === '' && index > 0) {
      kitInputRefs.current[index - 1]?.focus();
    }
  };

  const handleCharPaste = (index, e) => {
    e.preventDefault();
    let pastedData = e.clipboardData.getData('text').toUpperCase().replace(/[^A-Z0-9]/g, '');

    // Strip leading "S" if present (user pasted full code like "S-7FFN-DHWB-WJLG-3NX7")
    if (pastedData.startsWith('S') && pastedData.length > 16) {
      pastedData = pastedData.slice(1);
    }

    const newChars = [...kitChars];
    for (let i = 0; i < pastedData.length && index + i < 16; i++) {
      newChars[index + i] = pastedData[i];
    }
    setKitChars(newChars);

    // Focus the next empty input or the last one
    const nextEmptyIndex = newChars.findIndex((char, idx) => idx >= index && !char);
    if (nextEmptyIndex !== -1) {
      kitInputRefs.current[nextEmptyIndex]?.focus();
    } else {
      kitInputRefs.current[15]?.focus();
    }
  };

  const isKitIdComplete = () => kitChars.every(char => char.length === 1);

  // Helper function to ensure phone number starts with +1
  const ensurePhonePlusOne = (phoneNumber) => {
    if (!phoneNumber) return '';
    const trimmedPhone = phoneNumber.trim();
    if (!trimmedPhone.startsWith('+1')) {
      return `+1${trimmedPhone}`;
    }
    return trimmedPhone;
  };

  const handleAddressSelect = (address) => {
    const streetAddress = address.addressLabel || '';
    const city = address.city || address.locality || '';
    const zip = address.postalCode || address.zipCode || '';

    let stateAbbr = address.state || address.stateCode || '';
    const stateMap = {
      'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
      'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
      'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
      'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
      'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
      'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
      'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
      'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
      'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
      'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
    };

    let country = 'US';
    if (address.country === 'US' || address.countryCode === 'US') {
      country = 'US';
    } else if (address.country === 'Canada' || address.countryCode === 'CA' || address.countryCode === 'CAN') {
      country = 'CA';
    } else if (address.country === 'United Kingdom' || address.countryCode === 'UK' || address.countryCode === 'GB' || address.countryCode === 'GBR') {
      country = 'UK';
    } else {
      country = address.country || address.countryName || 'US';
    }

    if (country === 'US' && stateAbbr.length > 2) {
      stateAbbr = stateMap[stateAbbr] || stateAbbr;
    }

    isSelectingAddress.current = true;

    setCustomerInfo({
      ...customerInfo,
      address1: streetAddress,
      city: city,
      state: stateAbbr,
      zip: zip,
      country: country
    });

    setErrors(prevErrors => {
      const newErrors = { ...prevErrors };
      if (streetAddress) delete newErrors.address1;
      if (city) delete newErrors.city;
      if (stateAbbr) delete newErrors.state;
      if (zip) delete newErrors.zip;
      if (country) delete newErrors.country;
      return newErrors;
    });

    setSuggestions([]);
    setShowSuggestions(false);

    if (addressInputRef.current) {
      addressInputRef.current.blur();
    }
  };

  const postRegistrationData = async (id, signatures = {}) => {
    setIsLoading(true);
    setKitError('');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/kit_registration/${id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'accept': 'application/json',
          },
          body: JSON.stringify({
            customerInfo,
            qualificationSigned: signatures.qualificationSigned || qualificationSigned,
            authorizationSigned: signatures.authorizationSigned || authorizationSigned,
            hipaaSigned: signatures.hipaaSigned || hipaaSigned,
          })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Registration successful:', data);
      return data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const validateKit = async (id) => {
    setIsLoading(true);
    setKitError('');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/kit_registration/${id}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'accept': 'application/json',
          }
        }
      );

      const data = await response.json();
      if (response.status !== 200 || !data) {
        setKitError(data?.message || 'Kit ID not found in the system. Please check and try again.');
        setIsLoading(false);
        return false;
      }

      if (data.kit_invalid) {
        setKitError(`This kit is not valid. Please contact customer support for further assistance.`);
        setIsLoading(false);
        return false;
      }

      setKitData({
        id: id.toUpperCase(),
        type: data.kitType || 'Cancer Screen',
        need_qualification: data.need_qualification,
        need_authorization: data.need_authorization,
        need_hipaa_authorization: data.need_hipaa_authorization,
        hipaa_authorization_partner_name: data.hipaa_authorization_partner_name,
        hipaa_authorization_address: data.hipaa_authorization_address,
        hipaa_authorization_email: data.hipaa_authorization_email,
        kit_registered: data.kit_registered,
        kit_processed: data.kit_processed,
        kit_status: data.kit_status,
        hipaa_signed: data.hipaa_signed,
        results_send_patient: data.results_send_patient,
      });

      // Pre-fill customer information if available
      if (data.customer_info_prefill) {
        const prefill = data.customer_info_prefill;
        const hasActualData = prefill.first_name || prefill.last_name || prefill.email ||
                              prefill.address1 || prefill.phone || prefill.dob || prefill.sex_at_birth;
        setHasCustomerDataPrefill(!!hasActualData);

        let formattedDob = '';
        if (prefill.dob) {
          const dobDate = new Date(prefill.dob);
          if (!isNaN(dobDate.getTime())) {
            const month = String(dobDate.getUTCMonth() + 1).padStart(2, '0');
            const day = String(dobDate.getUTCDate()).padStart(2, '0');
            const year = dobDate.getUTCFullYear();
            formattedDob = `${month}/${day}/${year}`;
          }
        }

        setCustomerInfo({
          firstName: prefill.first_name || '',
          lastName: prefill.last_name || '',
          address1: prefill.address1 || '',
          address2: prefill.address2 || '',
          city: prefill.city || '',
          state: prefill.state || '',
          zip: prefill.zip || '',
          country: prefill.country || 'US',
          phone: ensurePhonePlusOne(prefill.phone || ''),
          email: prefill.email || '',
          confirmEmail: prefill.email || '',
          dateOfBirth: formattedDob,
          sexAtBirth: prefill.sex_at_birth || ''
        });

        setErrors({});
      } else {
        setHasCustomerDataPrefill(false);
      }

      setIsLoading(false);

      const hasDocumentsNeeded = data.need_qualification || data.need_authorization || data.need_hipaa_authorization;
      const hasCustomerInfo = data.customer_info_prefill && (
        data.customer_info_prefill.first_name ||
        data.customer_info_prefill.last_name ||
        data.customer_info_prefill.email ||
        data.customer_info_prefill.address1 ||
        data.customer_info_prefill.phone ||
        data.customer_info_prefill.dob ||
        data.customer_info_prefill.sex_at_birth
      );

      // Determine scenario and set appropriate state
      if (data.kit_registered) {
        // All registered kits show the status landing page
        setShowStatusLanding(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (!data.kit_registered && hasCustomerInfo && hasDocumentsNeeded) {
        setIsReadOnlyMode(true);
        setStep(1);
      } else if (!data.kit_registered && !hasCustomerInfo && hasDocumentsNeeded) {
        setIsReadOnlyMode(false);
        setStep(1);
      } else {
        setIsReadOnlyMode(false);
        setStep(1);
      }
      return true;

    } catch (error) {
      setIsLoading(false);
      console.error('Kit validation error:', error);

      if (error.message.includes('Failed to fetch')) {
        setKitError('Cannot connect to server. Please check your internet connection or try again later.');
      } else {
        setKitError(`Error: ${error.message}`);
      }

      return false;
    }
  };

  const handleKitSubmit = () => {
    if (isKitIdComplete()) {
      validateKit(kitId);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!customerInfo.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!customerInfo.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!customerInfo.address1.trim()) newErrors.address1 = 'Address is required';
    if (!customerInfo.city.trim()) newErrors.city = 'City is required';
    if (!customerInfo.state.trim()) newErrors.state = 'State is required';
    if (!customerInfo.zip.trim()) newErrors.zip = 'ZIP code is required';
    if (!customerInfo.country.trim()) newErrors.country = 'Country is required';
    if (!customerInfo.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!isPossibleNumber(customerInfo.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!customerInfo.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(customerInfo.email)) newErrors.email = 'Invalid email format';
    if (!customerInfo.confirmEmail.trim()) newErrors.confirmEmail = 'Please re-enter your email';
    else if (customerInfo.email !== customerInfo.confirmEmail) newErrors.confirmEmail = 'Emails do not match';
    if (!customerInfo.dateOfBirth || customerInfo.dateOfBirth.trim() === '') {
      newErrors.dateOfBirth = 'Date of birth is required';
    } else if (customerInfo.dateOfBirth.length !== 10) {
      newErrors.dateOfBirth = 'Please enter a complete date (MM/DD/YYYY)';
    } else {
      const [month, day, year] = customerInfo.dateOfBirth.split('/').map(num => parseInt(num, 10));
      const birthDate = new Date(year, month - 1, day);

      if (isNaN(birthDate.getTime()) || month < 1 || month > 12 || day < 1 || day > 31) {
        newErrors.dateOfBirth = 'Please enter a valid date';
      } else {
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }

        if (age < 21) {
          newErrors.dateOfBirth = 'You must be 21 years or older';
        } else if (age > 120) {
          newErrors.dateOfBirth = 'Please enter a valid date of birth';
        }
      }
    }
    if (!customerInfo.sexAtBirth) newErrors.sexAtBirth = 'Sex at birth is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const isAuthorizationNeeded = () => {
    if (!kitData) return true;
    return kitData.need_authorization || kitData.need_qualification || kitData.need_hipaa_authorization;
  };

  const handleCustomerInfoNext = () => {
    if (validateForm()) {
      if (isAuthorizationNeeded()) {
        setStep(2);
      } else {
        handleDirectSubmit();
      }
    }
  };

  const handleDirectSubmit = async () => {
    try {
      if (!kitData?.kit_registered) {
        await postRegistrationData(kitId);
      }
      setCompletedKitId(kitData?.id || kitId);
      setRegistrationComplete(true);
      resetForm();
    } catch (error) {
      console.error('Registration failed:', error);
      alert('Registration failed. Please try again.');
    }
  };

  const handleAgreementSubmit = () => {
    const hasRequiredDocuments = kitData.need_qualification || kitData.need_authorization;
    const needsSignature = hasRequiredDocuments || (kitData.need_hipaa_authorization && hipaaOptIn);

    // Validate agreement checkbox (only required when there are required documents)
    if (hasRequiredDocuments && !agreedToTerms) {
      alert('Please agree to the terms and provide your signature.');
      return;
    }

    // Validate signature (required when there are required docs or HIPAA is opted in)
    if (needsSignature) {
      if (!signature.trim()) {
        alert('Please provide your signature.');
        return;
      }

      const expectedFullName = `${customerInfo.firstName.trim()} ${customerInfo.lastName.trim()}`;
      if (signature.trim().toLowerCase() !== expectedFullName.toLowerCase()) {
        alert(`Signature must match your full name: ${expectedFullName}`);
        return;
      }
    }

    // If HIPAA is needed but user declined, show warning before proceeding
    if (kitData.need_hipaa_authorization && !hipaaOptIn) {
      setShowHipaaWarning(true);
      return;
    }

    submitAgreement();
  };

  const submitAgreement = () => {
    const hasRequiredDocuments = kitData.need_qualification || kitData.need_authorization;

    const signatures = {
      qualificationSigned: hasRequiredDocuments,
      authorizationSigned: hasRequiredDocuments,
      hipaaSigned: kitData.need_hipaa_authorization ? hipaaOptIn : false,
    };

    if (signatures.qualificationSigned) setQualificationSigned(true);
    if (signatures.authorizationSigned) setAuthorizationSigned(true);
    if (signatures.hipaaSigned) setHipaaSigned(true);

    handleFinalSubmit(signatures);
  };

  const handleFinalSubmit = async (signatures = {}) => {
    try {
      await postRegistrationData(kitId, signatures);
      setCompletedKitId(kitData.id);
      setRegistrationComplete(true);
      resetForm();
    } catch (error) {
      console.error('Registration error:', error);
      alert('Error registering kit. Please try again.');
    }
  };

  const resetForm = () => {
    setStep(0);
    setKitId('');
    setKitChars(Array(16).fill(''));
    setKitData(null);
    setCustomerInfo({
      firstName: '',
      lastName: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      country: 'US',
      phone: '',
      email: '',
      confirmEmail: '',
      dateOfBirth: '',
      sexAtBirth: ''
    });
    setAgreedToTerms(false);
    setSignature('');
    setQualificationSigned(false);
    setAuthorizationSigned(false);
    setHipaaSigned(false);
    setErrors({});
    setIsReadOnlyMode(false);
    setHasCustomerDataPrefill(false);
    setHipaaOptIn(false);
    setShowHipaaWarning(false);
    setShowStatusLanding(false);
  };

  const usStates = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ].map(state => ({ value: state, label: state }));

  const sexOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Intersex', label: 'Intersex' }
  ];

  const getDynamicHipaaText = () => {
    if (!kitData) return DOCUMENT_TEXTS.hipaa_authorization_form;

    let text = DOCUMENT_TEXTS.hipaa_authorization_form;

    if (kitData.hipaa_authorization_partner_name) {
      text = text.replace('(name) ­­­­­­­­­­­­­­­­­­­_______________________', kitData.hipaa_authorization_partner_name);
    }

    if (kitData.hipaa_authorization_address) {
      text = text.replace('(physical address) ___________________', kitData.hipaa_authorization_address);
    }

    if (kitData.hipaa_authorization_email) {
      text = text.replace('with an email address of ______________', `with an email address of ${kitData.hipaa_authorization_email}`);
    }

    return text;
  };

  // Render character input groups for kit ID
  const renderKitIdInputs = () => {
    const groups = [
      kitChars.slice(0, 4),
      kitChars.slice(4, 8),
      kitChars.slice(8, 12),
      kitChars.slice(12, 16)
    ];

    return (
      <div className="code-input-display">
        <span className="code-prefix">S</span>
        <span className="code-separator">-</span>
        {groups.map((group, groupIndex) => (
          <div key={groupIndex} style={{ display: 'flex', alignItems: 'center' }}>
            <div className="code-group">
              {group.map((char, charIndex) => {
                const globalIndex = groupIndex * 4 + charIndex;
                return (
                  <input
                    key={globalIndex}
                    ref={el => kitInputRefs.current[globalIndex] = el}
                    type="text"
                    className="code-char"
                    maxLength={1}
                    value={char}
                    onChange={(e) => handleCharInput(globalIndex, e.target.value)}
                    onKeyDown={(e) => handleCharKeyDown(globalIndex, e)}
                    onPaste={(e) => handleCharPaste(globalIndex, e)}
                    placeholder="#"
                    disabled={isLoading}
                  />
                );
              })}
            </div>
            {groupIndex < 3 && <span className="code-separator">-</span>}
          </div>
        ))}
      </div>
    );
  };

  // Main Render
  return (
    <MantineProvider>
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        {!showStatusLanding && <Header />}

        <main style={{ flex: 1 }}>
          {!showStatusLanding && <Hero />}

          {/* Kit Status Landing Page for registered kits */}
          {showStatusLanding && kitData?.kit_registered && (
            <KitStatusLanding
              firstName={customerInfo.firstName}
              kitStatus={kitData.kit_status}
              partnerName={kitData.hipaa_authorization_partner_name}
              hipaaSigned={kitData.hipaa_signed}
              resultsSendPatient={kitData.results_send_patient}
            />
          )}

          {/* Step 0: Kit ID Entry */}
          {!registrationComplete && !showStatusLanding && step === 0 && (
            <>
              <section className="banner-section">
                <div className="container">
                  <img src={BANNER_URL} alt="Registration Required" />
                </div>
              </section>

              <section className="code-entry-section">
                <div className="container">
                  <h2>Enter Your Registration Code</h2>

                  <div className="code-entry-container">
                    <div className="code-input-wrapper">
                      <label>Enter the code from your registration card</label>
                      {renderKitIdInputs()}
                      <button
                        type="button"
                        className="submit-btn"
                        onClick={handleKitSubmit}
                        disabled={isLoading || !isKitIdComplete()}
                      >
                        {isLoading ? 'Validating...' : 'Continue'}
                      </button>
                    </div>

                    {isLoading && (
                      <Center py="md" style={{ marginTop: 20 }}>
                        <Group>
                          <Loader size="sm" />
                          <Text>Validating kit...</Text>
                        </Group>
                      </Center>
                    )}

                    {kitError && (
                      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" style={{ marginTop: 20 }}>
                        {kitError}
                      </Alert>
                    )}

                    <div className="qr-alert">
                      <div className="qr-alert-content">
                        <h3>Don't want to type your code?</h3>
                        <p>Scan the QR code on the back of your registration card with your smart phone to skip this step and go straight to registration.</p>
                      </div>
                      <div className="qr-alert-image">
                        <span className="scan-bubble">Scan your card!</span>
                        <img src={QR_SCAN_URL} alt="Scan QR Code" />
                      </div>
                    </div>

                    <div className="instructions-link-container">
                      <Link to="/instructions" className="instructions-link">Click for step-by-step instructions</Link>
                    </div>

                    <NeedHelp />
                  </div>
                </div>
              </section>
            </>
          )}

          {/* Step 1: Customer Information */}
          {!registrationComplete && !showStatusLanding && step === 1 && (
            <div className="content-container" style={{ paddingTop: 20, paddingBottom: 40 }}>
              <div className="form-section">
                <h2>Customer Information</h2>

                {isReadOnlyMode && (
                  <Alert icon={<IconAlertCircle size={16} />} color="orange" variant="light" mb="lg">
                    Your information is locked. If you need to make changes, chat with us below or email us at <a href={`mailto:hello@cancerchecklabs.com?subject=Registration Help: ${kitData?.id || ''}`} style={{ color: 'inherit', textDecoration: 'underline' }}>hello@cancerchecklabs.com</a>.
                  </Alert>
                )}

                <form onSubmit={(e) => e.preventDefault()}>
                  {/* Name Row */}
                  <div className="form-row">
                    <div className={`form-group ${errors.firstName ? 'error' : ''}`}>
                      <label>First Name<span className="required">*</span></label>
                      <input
                        type="text"
                        value={customerInfo.firstName}
                        onChange={(e) => setCustomerInfo({...customerInfo, firstName: e.target.value.replace(/\b\w/g, l => l.toUpperCase())})}
                        placeholder="Enter your first name"
                        readOnly={isReadOnlyMode}
                        style={isReadOnlyMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : undefined}
                      />
                      {errors.firstName && <span className="error-message">{errors.firstName}</span>}
                    </div>
                    <div className={`form-group ${errors.lastName ? 'error' : ''}`}>
                      <label>Last Name<span className="required">*</span></label>
                      <input
                        type="text"
                        value={customerInfo.lastName}
                        onChange={(e) => setCustomerInfo({...customerInfo, lastName: e.target.value.replace(/\b\w/g, l => l.toUpperCase())})}
                        placeholder="Enter your last name"
                        readOnly={isReadOnlyMode}
                        style={isReadOnlyMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : undefined}
                      />
                      {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                    </div>
                  </div>

                  {/* Address Row 1 */}
                  <div className="form-row single">
                    <div className={`form-group ${errors.address1 ? 'error' : ''}`} style={{ position: 'relative' }}>
                      <label>Address Line 1<span className="required">*</span></label>
                      <input
                        ref={addressInputRef}
                        type="text"
                        value={customerInfo.address1}
                        onChange={(e) => setCustomerInfo({...customerInfo, address1: e.target.value})}
                        onFocus={() => {
                          if (customerInfo.address1.length >= 3 && suggestions.length > 0) {
                            setShowSuggestions(true);
                          }
                        }}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                        placeholder="Street address"
                        readOnly={isReadOnlyMode}
                        style={isReadOnlyMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : undefined}
                      />
                      {errors.address1 && <span className="error-message">{errors.address1}</span>}

                      {showSuggestions && (suggestions.length > 0 || loading) && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          zIndex: 1000,
                          maxHeight: '200px',
                          overflowY: 'auto',
                          background: 'white',
                          border: '1px solid #c5d4d8',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}>
                          {loading && (
                            <div style={{ padding: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Loader size="sm" />
                              <Text size="sm">Searching addresses...</Text>
                            </div>
                          )}
                          {suggestions.map((address, index) => (
                            <div
                              key={index}
                              style={{
                                padding: '12px 16px',
                                cursor: 'pointer',
                                borderBottom: index < suggestions.length - 1 ? '1px solid #e0e0e0' : 'none'
                              }}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handleAddressSelect(address)}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                              <Text size="sm">{address.formattedAddress}</Text>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Address Row 2 */}
                  <div className="form-row single">
                    <div className="form-group">
                      <label>Address Line 2</label>
                      <input
                        type="text"
                        value={customerInfo.address2}
                        onChange={(e) => setCustomerInfo({...customerInfo, address2: e.target.value})}
                        placeholder="Apartment, suite, unit, etc. (optional)"
                        readOnly={isReadOnlyMode}
                        style={isReadOnlyMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : undefined}
                      />
                    </div>
                  </div>

                  {/* City/State Row */}
                  <div className="form-row">
                    <div className={`form-group ${errors.city ? 'error' : ''}`}>
                      <label>City<span className="required">*</span></label>
                      <input
                        type="text"
                        value={customerInfo.city}
                        onChange={(e) => setCustomerInfo({...customerInfo, city: e.target.value})}
                        placeholder="Enter your city"
                        readOnly={isReadOnlyMode}
                        style={isReadOnlyMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : undefined}
                      />
                      {errors.city && <span className="error-message">{errors.city}</span>}
                    </div>
                    <div className={`form-group ${errors.state ? 'error' : ''}`}>
                      <label>State/Province<span className="required">*</span></label>
                      {customerInfo.country === 'US' && !isReadOnlyMode ? (
                        <Select
                          placeholder="Select state"
                          data={usStates}
                          value={customerInfo.state}
                          onChange={(value) => setCustomerInfo({...customerInfo, state: value})}
                          searchable
                          styles={{
                            input: {
                              padding: '12px 16px',
                              fontSize: '1rem',
                              border: errors.state ? '2px solid #d50434' : '2px solid #c5d4d8',
                              borderRadius: '8px',
                              height: 'auto',
                              minHeight: '48px'
                            }
                          }}
                        />
                      ) : (
                        <input
                          type="text"
                          value={customerInfo.state}
                          onChange={(e) => setCustomerInfo({...customerInfo, state: e.target.value})}
                          placeholder="Enter your state or province"
                          readOnly={isReadOnlyMode}
                          style={isReadOnlyMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : undefined}
                        />
                      )}
                      {errors.state && <span className="error-message">{errors.state}</span>}
                    </div>
                  </div>

                  {/* Postal/Country Row */}
                  <div className="form-row">
                    <div className={`form-group ${errors.zip ? 'error' : ''}`}>
                      <label>Postal Code<span className="required">*</span></label>
                      <input
                        type="text"
                        value={customerInfo.zip}
                        onChange={(e) => setCustomerInfo({...customerInfo, zip: e.target.value})}
                        placeholder="Enter your postal code"
                        readOnly={isReadOnlyMode}
                        style={isReadOnlyMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : undefined}
                      />
                      {errors.zip && <span className="error-message">{errors.zip}</span>}
                    </div>
                    <div className={`form-group ${errors.country ? 'error' : ''}`}>
                      <label>Country<span className="required">*</span></label>
                      <input
                        type="text"
                        value={customerInfo.country}
                        onChange={(e) => {
                          const value = e.target.value;
                          const code = getCode(value);
                          setCustomerInfo({...customerInfo, country: code || value});
                        }}
                        placeholder="Enter your country"
                        readOnly={isReadOnlyMode}
                        style={isReadOnlyMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : undefined}
                      />
                      {errors.country && <span className="error-message">{errors.country}</span>}
                    </div>
                  </div>

                  {/* Phone Row */}
                  <div className="form-row single">
                    <div className={`form-group ${errors.phone ? 'error' : ''}`}>
                      <label>Phone Number<span className="required">*</span></label>
                      <PhoneInput
                        defaultCountry="us"
                        placeholder="+1 (555) 123-4567"
                        value={customerInfo.phone}
                        onChange={(value) => setCustomerInfo({...customerInfo, phone: value})}
                        disabled={isReadOnlyMode}
                        style={{ width: '100%' }}
                        inputStyle={{
                          width: '100%',
                          height: '48px',
                          fontSize: '1rem',
                          border: errors.phone ? '2px solid #d50434' : '2px solid #c5d4d8',
                          borderRadius: '8px',
                          padding: '0 16px',
                          backgroundColor: isReadOnlyMode ? '#f5f5f5' : 'white',
                          cursor: isReadOnlyMode ? 'not-allowed' : 'text'
                        }}
                        containerStyle={{ width: '100%' }}
                      />
                      {errors.phone && <span className="error-message">{errors.phone}</span>}
                    </div>
                  </div>

                  {/* Email Row */}
                  <div className="form-row">
                    <div className={`form-group ${errors.email ? 'error' : ''}`}>
                      <label>Email<span className="required">*</span></label>
                      <input
                        type="email"
                        value={customerInfo.email}
                        onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                        placeholder="your@email.com"
                        readOnly={isReadOnlyMode}
                        style={isReadOnlyMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : undefined}
                      />
                      {errors.email && <span className="error-message">{errors.email}</span>}
                    </div>
                    <div className={`form-group ${errors.confirmEmail ? 'error' : ''}`}>
                      <label>Re-Enter Email<span className="required">*</span></label>
                      <input
                        type="email"
                        value={customerInfo.confirmEmail}
                        onChange={(e) => setCustomerInfo({...customerInfo, confirmEmail: e.target.value})}
                        placeholder="Confirm your email"
                        readOnly={isReadOnlyMode}
                        style={isReadOnlyMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : undefined}
                      />
                      {errors.confirmEmail && <span className="error-message">{errors.confirmEmail}</span>}
                    </div>
                  </div>

                  {/* DOB/Sex Row */}
                  <div className="form-row">
                    <div className={`form-group ${errors.dateOfBirth ? 'error' : ''}`}>
                      <label>Date of Birth<span className="required">*</span></label>
                      <input
                        type="text"
                        value={customerInfo.dateOfBirth}
                        onChange={(e) => {
                          let value = e.target.value.replace(/\D/g, '');
                          if (value.length >= 2) value = value.slice(0, 2) + '/' + value.slice(2);
                          if (value.length >= 5) value = value.slice(0, 5) + '/' + value.slice(5);
                          if (value.length > 10) value = value.slice(0, 10);
                          setCustomerInfo({...customerInfo, dateOfBirth: value});
                        }}
                        placeholder="MM/DD/YYYY"
                        maxLength={10}
                        readOnly={isReadOnlyMode}
                        style={isReadOnlyMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : undefined}
                      />
                      {errors.dateOfBirth && <span className="error-message">{errors.dateOfBirth}</span>}
                    </div>
                    <div className={`form-group ${errors.sexAtBirth ? 'error' : ''}`}>
                      <label>Sex at Birth<span className="required">*</span></label>
                      {isReadOnlyMode ? (
                        <input
                          type="text"
                          value={customerInfo.sexAtBirth}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      ) : (
                        <Select
                          placeholder="Select..."
                          data={sexOptions}
                          value={customerInfo.sexAtBirth}
                          onChange={(value) => setCustomerInfo({...customerInfo, sexAtBirth: value})}
                          styles={{
                            input: {
                              padding: '12px 16px',
                              fontSize: '1rem',
                              border: errors.sexAtBirth ? '2px solid #d50434' : '2px solid #c5d4d8',
                              borderRadius: '8px',
                              height: 'auto',
                              minHeight: '48px'
                            }
                          }}
                        />
                      )}
                      {errors.sexAtBirth && <span className="error-message">{errors.sexAtBirth}</span>}
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="button-row">
                    <button type="button" className="btn btn-back" onClick={() => setStep(0)}>
                      Back
                    </button>
                    <button type="button" className="btn btn-continue" onClick={handleCustomerInfoNext}>
                      {isReadOnlyMode ? (isAuthorizationNeeded() ? 'Continue to Agreement' : 'Done') : (isAuthorizationNeeded() ? 'Save and Continue' : 'Save and Submit')}
                    </button>
                  </div>
                </form>
              </div>

              <NeedHelp />
            </div>
          )}

          {/* Step 2: Consent Agreements */}
          {!registrationComplete && !showStatusLanding && step === 2 && kitData && (
            <div className="content-container" style={{ paddingTop: 20, paddingBottom: 40 }}>
              {/* Registration Summary */}
              <div className="section-box">
                <h2>Registration Summary</h2>
                <div className="kit-info">
                  <div className="kit-info-item">
                    <label>Kit ID</label>
                    <span>{kitData.id}</span>
                  </div>
                  <div className="kit-info-row">
                    <div className="kit-info-item">
                      <label>First Name</label>
                      <span>{customerInfo.firstName}</span>
                    </div>
                    <div className="kit-info-item">
                      <label>Last Name</label>
                      <span>{customerInfo.lastName}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Test Qualification */}
              {(kitData.need_qualification || kitData.need_authorization) && (
                <div className="section-box">
                  <h2>Test Qualification and Authorization</h2>
                  <h3>
                    You must meet specific qualifications to ensure you fit the necessary criteria to take the Cancer Check test.
                  </h3>
                  <div className="document-list">
                    <div className="document-item">
                      <span className="document-name">Test Qualification and Authorization Form</span>
                      <button className="btn-view" onClick={() => setModalOpen('test_qualification')}>
                        View Document
                      </button>
                    </div>
                  </div>
                  <label className="checkbox-container qualification-checkbox">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                    />
                    <span className="checkbox-label">
                      I agree to the terms and conditions in the test qualification and authorization form.<span style={{ color: '#d50434', marginLeft: '2px' }}>*</span>
                    </span>
                  </label>
                </div>
              )}

              {/* HIPAA Authorization (Optional) */}
              {kitData.need_hipaa_authorization && (
                <div className="section-box hipaa-optional-section">
                  <h2>HIPAA Authorization</h2>
                  <h3>
                    Signing the HIPAA Authorization allows Cancer Check Labs to share your
                    test results with <strong>{kitData.hipaa_authorization_partner_name}</strong>.
                  </h3>
                  <div className="document-list">
                    <div className="document-item">
                      <span className="document-name">HIPAA Authorization Form</span>
                      <button className="btn-view" onClick={() => setModalOpen('hipaa')}>
                        View Document
                      </button>
                    </div>
                  </div>
                  <label className="checkbox-container hipaa-checkbox">
                    <input
                      type="checkbox"
                      checked={hipaaOptIn}
                      onChange={(e) => setHipaaOptIn(e.target.checked)}
                    />
                    <span className="checkbox-label">
                      I authorize Cancer Check Labs to share my test results with{' '}
                      <strong>{kitData.hipaa_authorization_partner_name}</strong>.
                    </span>
                  </label>
                </div>
              )}


              {/* Electronic Signature — required when there are required docs OR when HIPAA is opted in */}
              {((kitData.need_qualification || kitData.need_authorization) || (kitData.need_hipaa_authorization && hipaaOptIn)) && (
                <div className={`section-box signature-section ${
                  (kitData.need_qualification || kitData.need_authorization) ? (agreedToTerms ? 'enabled' : '') : 'enabled'
                }`}>
                  <h2>Electronic Signature</h2>
                  <h3>Type your full name to provide electronic signature</h3>
                  <input
                    type="text"
                    className="signature-input"
                    value={signature}
                    onChange={(e) => setSignature(e.target.value)}
                    placeholder="Enter your full name"
                    disabled={(kitData.need_qualification || kitData.need_authorization) ? !agreedToTerms : false}
                  />
                </div>
              )}

              {/* Buttons */}
              {(() => {
                const hasRequiredDocs = kitData.need_qualification || kitData.need_authorization;
                const needsSignature = hasRequiredDocs || (kitData.need_hipaa_authorization && hipaaOptIn);
                const isDisabled = needsSignature ? (!agreedToTerms && hasRequiredDocs) || !signature.trim() : false;
                const buttonText = needsSignature ? 'I Agree' : 'Submit';
                return (
                  <div className="button-row">
                    <button type="button" className="btn btn-back" onClick={() => setStep(isReadOnlyMode || hasCustomerDataPrefill ? 0 : 1)}>
                      Back
                    </button>
                    <button
                      type="button"
                      className="btn btn-agree"
                      onClick={handleAgreementSubmit}
                      disabled={isDisabled}
                    >
                      {buttonText}
                    </button>
                  </div>
                );
              })()}

              <NeedHelp />

              {/* Document Modals */}
              <DocumentModal
                isOpen={modalOpen === 'test_qualification'}
                onClose={() => setModalOpen(null)}
                title="Test Qualification and Authorization Form"
                content={DOCUMENT_TEXTS.test_qualification_form}
              />
              <DocumentModal
                isOpen={modalOpen === 'hipaa'}
                onClose={() => setModalOpen(null)}
                title="HIPAA Authorization"
                content={getDynamicHipaaText()}
              />

              {/* HIPAA Decline Warning Modal */}
              {showHipaaWarning && (
                <div className="modal-overlay active" onClick={(e) => {
                  if (e.target === e.currentTarget) setShowHipaaWarning(false);
                }}>
                  <div className="modal hipaa-warning-modal">
                    <div className="modal-header">
                      <h3>HIPAA Authorization Not Selected</h3>
                      <button className="modal-close" onClick={() => setShowHipaaWarning(false)}>&times;</button>
                    </div>
                    <div className="modal-body">
                      <div className="hipaa-warning-icon">
                        <IconAlertCircle size={48} color="#e67e22" />
                      </div>
                      <p className="hipaa-warning-text">
                        Please note <strong>{kitData.hipaa_authorization_partner_name}</strong> has
                        requested to receive your test results. Without signing the HIPAA
                        authorization form, Cancer Check Labs will be unable to send your
                        results to <strong>{kitData.hipaa_authorization_partner_name}</strong>.
                      </p>
                      <p className="hipaa-warning-subtext">
                        You can still proceed with your registration. Your results will be
                        available directly from Cancer Check Labs.
                      </p>
                    </div>
                    <div className="hipaa-warning-actions">
                      <button
                        className="btn btn-back"
                        onClick={() => setShowHipaaWarning(false)}
                      >
                        Go Back
                      </button>
                      <button
                        className="btn btn-agree hipaa-proceed-btn"
                        onClick={() => {
                          setShowHipaaWarning(false);
                          submitAgreement();
                        }}
                      >
                        Continue Without HIPAA
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Success Screen */}
          {registrationComplete && (
            <div className="content-container" style={{ paddingTop: 40, paddingBottom: 40 }}>
              <div className="success-screen">
                <img
                  src={LOGO_URL}
                  alt="Cancer Check Labs"
                  style={{ width: '200px', height: 'auto', marginBottom: '30px' }}
                />

                <div className="success-icon">
                  <IconCheck size={40} color="white" />
                </div>

                <h1 className="success-title">Thank you for registering your kit</h1>

                <p className="success-message">
                  You will receive an email confirmation within 24 hours with details on how to schedule phlebotomy for your test.
                </p>

                <p className="success-contact">
                  If you have any questions please contact <a href={`mailto:hello@cancerchecklabs.com?subject=New Inquiry: ${completedKitId}`}>hello@cancerchecklabs.com</a>
                </p>

                <button
                  className="btn-register-another"
                  onClick={() => {
                    setRegistrationComplete(false);
                    resetForm();
                    setCompletedKitId('');
                  }}
                >
                  Register Another Kit
                </button>
              </div>

              <NeedHelp />
            </div>
          )}
        </main>

        <Footer />
      </div>
    </MantineProvider>
  );
};

// App wrapper with routing
const App = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<KitRegistrationApp />} />
        <Route path="/instructions" element={<Instructions />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
